import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import {
  TenderTypes,
  usePlans,
  useCompaniesPublic,
  useGetCompanyById,
  SecurityTypes,
} from '@web-kc/core/api';
import { useQueryParams } from '@web-kc/shared/helpers';
import { useRouter } from 'next/router';
import { useDebouncedCallback } from 'use-debounce';
import { useApp } from '@web-kc/core/providers';

type CriteriaPlanWithModal = TenderTypes['CriteriaPlan'] & { modal?: string };

interface PlanListContextValue {
  data?: TenderTypes['PageTenderPlanDto'];
  isLoading: boolean;
  searchFilters: CriteriaPlanWithModal;
  isCreateModalOpen: boolean;
  refetch: () => void;
  setSearchFilters: (
    filters: CriteriaPlanWithModal | any,
  ) => Promise<void> | any;
  handleChangePageNumber: (page: number) => void;
  handleCreatePlan: () => void;
  handleCreatePurchase: () => void;
  handleRowClick: (planId: number) => void;
  handleCloseModal: (created: boolean) => void;
  companies?: SecurityTypes['PageCompanyDto'];
  isLoadingCompanies?: boolean;
  companyById?: SecurityTypes['CompanyDto'];
  debouncedSearchCompanies: (keyword: string) => void;
}

const PlanListContext = createContext<PlanListContextValue | undefined>(
  undefined,
);

interface PlanListProviderProps {
  children: ReactNode;
}

export const PlanListProvider: React.FC<PlanListProviderProps> = ({
  children,
}) => {
  const { push, pathname, query } = useRouter();
  const isRti = useApp() === 'rti';
  const [searchFilters, setSearchFilters] =
    useQueryParams<CriteriaPlanWithModal>({
      page: 0,
      size: 10,
      sort: 'CREATED_DESC',
    });

  const [companiesSearchKeyword, setCompaniesSearchKeyword] = useState('');

  // Устанавливаем term: 'YEARLY' для RTI при первом рендере, если его нет
  useEffect(() => {
    if (isRti && !searchFilters.term) {
      setSearchFilters({
        ...searchFilters,
        term: 'YEARLY' as never,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRti]); // Запускаем только при изменении isRti (один раз при монтировании)

  const { data, isLoading, refetch } = usePlans(
    searchFilters,
    pathname.startsWith('/plans'),
  );

  // Запрос компании по ID из URL для восстановления названия
  const { data: companyById } = useGetCompanyById(
    {
      full: false,
      id: Number(query?.company),
    },
    !!query?.company,
  );

  // Дебаунс поиск компаний
  const debouncedSearchCompanies = useDebouncedCallback((keyword: string) => {
    setCompaniesSearchKeyword(keyword);
  }, 1000);

  // Запрос для поиска компаний
  const { data: companies, isLoading: isLoadingCompanies } = useCompaniesPublic(
    {
      page: 0,
      size: 100,
      keyword: companiesSearchKeyword,
    },
    !!companiesSearchKeyword,
  );

  const isCreateModalOpen = searchFilters.modal === 'create';

  const handleChangePageNumber = (page: number) => {
    setSearchFilters({
      ...searchFilters,
      page: page - 1,
    }).then();
  };

  const handleCreatePlan = () => {
    setSearchFilters({ ...searchFilters, modal: 'create' });
  };

  const handleCreatePurchase = () => {
    push('/cabinet/plans/tenders');
  };

  const handleRowClick = (planId: number) => {
    push(`plans/${planId}`);
  };

  const handleCloseModal = (created: boolean) => {
    if (created) refetch();
    setSearchFilters({ ...searchFilters, modal: undefined });
  };

  const value: PlanListContextValue = {
    data,
    isLoading,
    searchFilters,
    isCreateModalOpen,
    refetch,
    setSearchFilters,
    handleChangePageNumber,
    handleCreatePlan,
    handleCreatePurchase,
    handleRowClick,
    handleCloseModal,
    companies,
    isLoadingCompanies,
    companyById,
    debouncedSearchCompanies,
  };

  return (
    <PlanListContext.Provider value={value}>
      {children}
    </PlanListContext.Provider>
  );
};

export const usePlanList = (): PlanListContextValue => {
  const context = useContext(PlanListContext);
  if (context === undefined) {
    throw new Error('usePlanList must be used within a PlanListProvider');
  }
  return context;
};

export default PlanListProvider;
